import type { PlacementRefusal, PlacementRefusalReason, ReviewDetail, ReviewItem, ReviewKind, ReviewNoticeCode } from '../api/types'
import { size, sizeMb } from '../ui/format'
import { stepWord, validationCheckDescriptionWord } from './node'
import { ownerLabel } from './owner'

// 검토 대기의 낱말. 절 제목은 종류마다 하나, 알림은 코드마다 막힘 한 문장이다 — 코드 원값은 화면에 나오지 않는다.

export const KIND_ORDER: ReviewKind[] = ['signup_request', 'gpu_approval', 'lease_change', 'cluster_decision', 'node_offline', 'notice']

export const KIND_TITLE: Record<ReviewKind, string> = {
  signup_request: '가입 신청',
  gpu_approval: 'GPU 승인',
  lease_change: '변경 신청',
  cluster_decision: '클러스터 결정',
  node_offline: '응답 없는 노드',
  notice: '알림',
}

const NOTICE: Record<ReviewNoticeCode, string> = {
  node_onboarding_failed: '설치에 실패했습니다. 다시 설치하세요.',
  node_check_failed: '장비 점검을 통과하지 못했습니다. 장비를 확인하고 운영 복귀를 예약하세요.',
  node_gpu_suspect: 'GPU 한 장을 임대에서 제외했습니다. 그 GPU 를 교체하세요.',
  node_gpu_held: 'GPU 를 연결하지 않고 부팅합니다. 시험할 GPU 를 고르세요.',
  node_crashed: '노드가 갑자기 멈춘 기록이 도착했습니다. 다시 설치하기 전에 노드 상세 고급의 크래시 기록을 확인하세요.',
  node_boot_disks_multiple: '부팅 디스크가 여럿으로 잡혔습니다. 프로필의 디스크 필터를 고치고 다시 설치하세요.',
  node_install_profile_missing: '이 기종의 설치 프로필이 없습니다. 프로필에 기종을 지정하고 다시 설치하세요.',
  node_disk_report_missing: '설치 매체가 디스크를 보고하지 않았습니다. 새 ISO 로 다시 설치하세요.',
  node_verify_failing: '검증이 반복해서 실패합니다. 노드 상세의 검증에서 막힌 곳을 보고 고치세요. 고치면 스스로 다시 검증합니다.',
  node_install_unreachable: '설치는 끝났지만 노드의 관리 주소로 닿지 않습니다.',
  node_first_boot_stalled: '설치는 끝났지만 첫 부팅이 끝나지 않고 있습니다. 노드 이벤트에서 무엇을 기다리는지 확인하세요.',
  node_units_unsettled: '노드의 서비스 설정이 새 버전으로 바뀌지 않고 있습니다. 노드의 로그에서 원인을 확인하세요.',
  node_address_unconfirmed: '노드의 관리 주소로 닿지 않습니다. 노드의 관리 네트워크를 확인하세요.',
  node_agent_silent: '노드의 보고가 끊겼습니다. 노드의 네트워크를 확인하세요.',
  node_reboot_unreported: '재부팅한 노드가 돌아왔다고 보고하지 않습니다. 노드를 확인하세요.',
  node_gpu_unmeasured: 'GPU 한 장의 메모리 크기를 알아내지 못해 임대하지 않습니다. BIOS 에서 Above 4G Decoding 과 Resizable BAR 를 켜고 장비를 다시 부팅하세요.',
  node_onboarding_stalled: '온보딩이 오래 멈춰 있습니다. 노드 상세의 진행 기록에서 무엇을 기다리는지 확인하세요.',
  lease_failed: 'VM 을 만들지 못했습니다.',
  lease_awaiting_capacity: '자원이 부족해 VM 을 만들지 못하고 있습니다.',
  lease_volume_failed: 'VM 을 만들 때 요청한 디스크를 붙이지 못했습니다. VM 상세에서 디스크를 다시 추가하세요.',
  cluster_lease_settings_undecided: 'VM 을 놓을 스토리지가 아직 정해지지 않았습니다.',
  cluster_needs_qdevice: '노드 수가 짝수입니다. QDevice 를 설정하세요.',
  cluster_relink_stuck: '클러스터를 새 관리 주소로 옮기다 멈췄습니다. 노드를 확인하세요.',
}

/**
 * 알림 한 줄. 대부분 코드가 문장을 정하고, 몇은 사실을 문장에 싣는다(#325) —
 * 멈춘 온보딩은 넘어가지 못한 걸음을, 온보딩 중 점검 실패는 실패한 항목과 그 조치를,
 * 닿지 않는 노드는 시도한 주소를 말한다(#324·#489). 원인은 추측하지 않는다.
 * 점검 항목이 실린 실패는 원인을 고치고 다시 부팅하면 온보딩이 이어진다.
 */
export function noticeSentence(
  detail: Pick<ReviewDetail, 'code' | 'step' | 'failed_checks' | 'address' | 'observed_address' | 'relink_step' | 'node_name'>,
): string {
  const { code, step, failed_checks: checks } = detail
  if (!code) return '확인이 필요합니다.'
  if (code === 'cluster_relink_stuck' && detail.relink_step) {
    const node = detail.node_name ?? '노드'
    switch (detail.relink_step) {
      case 'relink':
        return '클러스터 설정을 새 관리 주소로 바꾸지 못했습니다. 클러스터의 정족수를 확인하세요.'
      case 'relink_local':
        if (!detail.node_name) {
          return '클러스터 멤버들이 새 관리 주소로 다시 붙지 못했습니다. 각 노드의 클러스터 연결과 HA 리소스를 확인하세요.'
        }
        return `${node} 가 새 관리 주소로 클러스터에 다시 붙지 못했습니다. ${node} 의 클러스터 연결을 확인하세요.`
      case 'drop_old_address':
        return `${node} 의 옛 관리 주소를 걷지 못했습니다. ${node} 의 네트워크 설정을 확인하세요.`
    }
  }
  if (code === 'node_install_unreachable' && detail.address) {
    return `설치는 끝났지만 ${detail.address} 로 닿지 않습니다.`
  }
  if (code === 'node_address_unconfirmed' && detail.address && detail.observed_address) {
    return `${detail.address} 로도, 노드가 알린 ${detail.observed_address} 로도 닿지 않습니다. 노드의 관리 네트워크를 확인하세요.`
  }
  if (code === 'node_onboarding_stalled' && step) {
    return `${stepWord(step)}에서 오래 멈춰 있습니다. 노드 상세의 진행 기록에서 무엇을 기다리는지 확인하세요.`
  }
  if (code === 'node_check_failed' && checks && checks.length > 0) {
    const said = checks.map(validationCheckDescriptionWord)
    // IOMMU 문장은 조치(펌웨어에서 켜고 재부팅)까지 이미 말한다.
    const tail = checks.includes('iommu_enabled') ? '' : ' 원인을 고치고 장비를 다시 부팅하세요.'
    return said.join(' ') + tail
  }
  return NOTICE[code]
}

// 배치가 노드를 쓰지 못한 이유(B-46). 부족은 남은 양과 요청한 양을 함께 말하고, 나머지는 범주 낱말 하나다.
const REFUSAL_WORD: Record<Exclude<PlacementRefusalReason, 'cores' | 'memory' | 'disk' | 'cards'>, string> = {
  no_storage: '디스크를 둘 저장소 없음',
  unmanaged: '기록에 없는 VM 이 돌고 있음',
  unmeasured: '자원을 알 수 없음',
  unusable: 'VM 을 둘 설정 없음',
  unreachable: '응답 없음',
  guest_net: '게스트 망 고장',
  race_room: '다른 신청이 먼저 자리를 가져감',
  race_card: '다른 신청이 먼저 GPU 를 가져감',
  unknown: '이유를 알 수 없음',
}

/** 노드 하나가 신청을 받지 못한 이유 한 줄 — `node-a · 최대 31코어 (32코어 필요)`. */
export function refusalLine(r: PlacementRefusal): string {
  const free = Math.max(0, r.free ?? 0)
  const want = r.want ?? 0
  switch (r.reason) {
    case 'cores':
      return `${r.node} · 최대 ${free}코어 (${want}코어 필요)`
    case 'memory':
      return `${r.node} · 메모리 최대 ${sizeMb(free)} (${sizeMb(want)} 필요)`
    case 'disk':
      return `${r.node} · 디스크 최대 ${size(free)} (${size(want)} 필요)`
    case 'cards':
      return `${r.node} · GPU 최대 ${free}장 (${want}장 필요)`
    default:
      return `${r.node} · ${REFUSAL_WORD[r.reason] ?? REFUSAL_WORD.unknown}`
  }
}

/** 항목 하나의 열쇠 — 같은 주체라도 국면이 다르면 다른 항목이다. */
export function reviewKey(item: Pick<ReviewItem, 'kind' | 'subject_id' | 'episode'>): string {
  return `${item.kind}:${item.subject_id}:${item.episode}`
}

// 여럿을 한 번에 하는 것은 그 항목 하나로 결정이 끝나는 조작뿐이다 — 신청의 승인·반려와 알림의 무시.
// 클러스터 결정·응답 없는 노드(제거와 무시 중 하나)·보존 기한(파기와 되살리기)은 선택지가 있거나 되돌릴 수 없어 한 건씩 한다.
const BATCH_KINDS: ReadonlySet<ReviewKind> = new Set<ReviewKind>(['signup_request', 'gpu_approval', 'lease_change', 'notice'])

export type BatchAction = 'approve' | 'reject' | 'dismiss'
const BATCH_ACTIONS: BatchAction[] = ['approve', 'reject', 'dismiss']

/** 체크박스가 서는 항목. 변경이 실리지 않은 변경 신청은 줄이 서지 않으므로 고를 수도 없다. */
export function batchable(item: ReviewItem): boolean {
  return BATCH_KINDS.has(item.kind) && (item.kind !== 'lease_change' || item.detail.change !== undefined)
}

/** 고른 항목 모두의 `actions` 가 허락하는 일괄 조작. 하나라도 허락하지 않으면 그 칩은 서지 않는다. */
export function batchActions(items: ReviewItem[]): BatchAction[] {
  if (items.length === 0) return []
  return BATCH_ACTIONS.filter((action) => items.every((item) => item.actions.includes(action)))
}

/** 일괄 반려가 사유를 받는가 — 가입 신청의 반려만 사유를 싣지 않는다. */
export function rejectNeedsReason(items: ReviewItem[]): boolean {
  return items.some((item) => item.kind !== 'signup_request')
}

/** 임대 항목의 사실 줄: 신청자 · 요청 사유 · 사양. 사유는 승인을 기다리는 신청이 적은 것이다(F-95). */
export function leaseFacts(item: ReviewItem): string {
  const d = item.detail
  const parts: string[] = []
  if (d.owner) parts.push(ownerLabel(d.owner) + (d.team ? ` · ${d.team.name}` : ''))
  if (d.purpose) parts.push(d.purpose)
  const spec: string[] = []
  if (d.cores !== undefined) spec.push(`${d.cores}코어`)
  if (d.memory_mb !== undefined) spec.push(sizeMb(d.memory_mb))
  if (d.disk_gb !== undefined) spec.push(size(d.disk_gb))
  if (d.gpus) spec.push(`GPU ${d.gpus}장${d.gpu_model ? ` ${d.gpu_model}` : ''}`)
  if (spec.length) parts.push(spec.join(' · '))
  return parts.join(' — ')
}
